import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MarketingFooter from '../MarketingFooter';
import MarketingNav from '../MarketingNav';

/**
 * Shared frame for the public HOWIE Companion pages (privacy, download).
 * These load without signing in: the Chrome Web Store, the desktop app's
 * heads-up line and IT admins link straight to them, often to an anchor.
 */
export function CompanionPublicPage({ children }: { children: React.ReactNode }) {
  const { hash } = useLocation();

  useEffect(() => {
    // React Router does not scroll to #anchors on load; deep links like /companion/privacy#in-a-meeting need it.
    if (!hash) return;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    target?.scrollIntoView();
  }, [hash]);

  return (
    <div className="relative isolate flex min-h-screen flex-col overflow-hidden bg-how-paper-soft text-how-space-deep">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-[42rem] bg-gradient-to-b from-transparent to-how-rose opacity-50"
        aria-hidden="true"
      />
      <MarketingNav actionLabel="Sign in" actionTo="/login" />

      <main className="relative z-10 mx-auto w-full max-w-[760px] px-6 pb-24 pt-14 text-landing-footer sm:px-10 sm:pt-20">
        {children}
      </main>

      <div className="relative z-10 mt-auto">
        <MarketingFooter />
      </div>
    </div>
  );
}

export function CompanionPageTitle({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="text-4xl leading-[1.08] tracking-[-0.02em] text-how-space-deep sm:text-5xl" style={{ fontFamily: 'var(--font-heading)' }}>
      {children}
    </h1>
  );
}

export function CompanionSection({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-12 scroll-mt-6 border-t border-how-space-deep/10 pt-10 sm:mt-14 sm:pt-12">
      <h2 className="text-2xl leading-tight tracking-[-0.01em] text-how-space-deep" style={{ fontFamily: 'var(--font-heading)' }}>{title}</h2>
      <div className="mt-4 space-y-4 text-base leading-7 text-how-space-mid">{children}</div>
    </section>
  );
}

export function SupportEmail() {
  return (
    <a className="underline" href="mailto:support@lightningstudios.ai">
      support@lightningstudios.ai
    </a>
  );
}
